const MEDIUM_KEYWORDS = /\b(penalty|cancel|fee|deadline|revision|late|forfeit|refund|within\s+\d+)\b/i;
const HIGH_KEYWORDS = /\b(arbitration|dispute|liability|indemn)\b/i;

const inferRisk = (text) => {
  if (HIGH_KEYWORDS.test(text)) return "high";
  if (MEDIUM_KEYWORDS.test(text)) return "medium";
  return "low";
};

const slugId = (text, index) => {
  const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40) || `term_${index}`;
  return `${base}_${index}`;
};

export function isInventedPlatformTerm(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return (
    /disputes?\s+unresolved\s+within\s+\d+\s+days/i.test(t) ||
    /escalate(?:s|d)?\s+to\s+pendpay\s+arbitration/i.test(t)
  );
}

export function deriveTermsFromContractText(contractText) {
  const text = String(contractText || "").trim();
  if (!text) {
    return [{
      id: "agreement",
      text: "Deliverables and payment per the agreed milestone schedule between both parties.",
      risk: "low",
    }];
  }

  let lines = text
    .split(/\n+/)
    .flatMap((line) => line.split(/(?=[•\-*]\s)/))
    .map((line) => line.replace(/^[\s•\-*]+/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter((line) => line.length > 2);

  if (lines.length <= 1) {
    lines = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 8);
  }

  if (!lines.length) lines = [text];

  return lines
    .map((chunk, i) => ({
      id: slugId(chunk, i),
      text: chunk,
      risk: inferRisk(chunk),
    }))
    .filter((t) => !isInventedPlatformTerm(t.text))
    .slice(0, 12);
}

export function resolveDealTerms(raw) {
  if (Array.isArray(raw?.terms) && raw.terms.length) {
    return raw.terms.filter((t) => !isInventedPlatformTerm(t?.text));
  }
  let terms = raw?.aiTerms ?? raw?.ai_terms;
  if (typeof terms === "string") {
    try { terms = JSON.parse(terms); } catch { terms = null; }
  }
  if (Array.isArray(terms) && terms.length) {
    return terms.filter((t) => !isInventedPlatformTerm(t?.text));
  }
  return deriveTermsFromContractText(raw?.contractText ?? raw?.contract_text ?? "");
}

export function parseDealMilestonesList(raw) {
  let milestones = raw?.milestones;
  if (typeof milestones === "string") {
    try { milestones = JSON.parse(milestones); } catch { milestones = []; }
  }
  return Array.isArray(milestones) ? milestones : [];
}

/** Human-readable milestone status derived from deal progress (not stale JSON `status` field). */
export function deriveMilestoneUi(index, raw, role = "sender") {
  const dealStatus = raw?.status;
  const current = Number(raw?.currentMilestone ?? raw?.current_milestone ?? 0);
  const ev = raw?.currentMilestoneEvent;
  const evIdx = ev?.milestoneIndex ?? ev?.milestone_index;
  const onCurrent = index === current;
  const evOnThis = onCurrent && (evIdx == null || evIdx === index);

  let label;

  if (dealStatus === "cancelled" || dealStatus === "expired") {
    label = "Cancelled";
  } else if (dealStatus === "pending_payment" || dealStatus === "pending_acceptance") {
    label = "Scheduled";
  } else if (dealStatus === "disputed" && onCurrent) {
    label = "Disputed";
  } else if (dealStatus === "completed" || index < current) {
    label = role === "receiver" ? "Paid" : "Released";
  } else if (dealStatus === "disputed") {
    label = "On hold";
  } else if (index > current) {
    label = "Upcoming";
  } else if (evOnThis && ev?.type === "claimed") {
    label = role === "sender" ? "Awaiting your approval" : "Awaiting approval";
  } else if (evOnThis && ev?.type === "rejected") {
    label = "In progress";
  } else {
    label = "In progress";
  }

  const tone =
    label === "Released" || label === "Paid" ? "success"
      : label === "Upcoming" || label === "Scheduled" || label === "On hold" ? "muted"
        : label === "Cancelled" ? "muted"
          : label.includes("approval") ? "gold"
            : label === "Disputed" ? "danger"
              : "info";

  return { label, tone };
}

export function milestoneReceiveRows(raw, receiverFeeNaira) {
  const milestones = parseDealMilestonesList(raw);
  const role = raw?.role || "receiver";

  const totalKobo = Number(raw?.amount) || 0;
  const receiverFeeKobo = Number(raw?.receiverFee ?? raw?.receiver_fee) || Math.round(receiverFeeNaira * 100);

  if (!milestones.length) {
    const receiveKobo = Math.max(0, totalKobo - receiverFeeKobo);
    return [{
      ms: "Full payment",
      pct: "100%",
      amt: Math.round(receiveKobo / 100),
      when: "Released when sender approves the milestone",
    }];
  }

  return milestones.map((m, i) => {
    const msKobo = Number(m.amount) || 0;
    const feeShare = totalKobo > 0 ? Math.round(receiverFeeKobo * (msKobo / totalKobo)) : 0;
    const receiveKobo = Math.max(0, msKobo - feeShare);
    const pct = totalKobo > 0 ? `${Math.round((msKobo / totalKobo) * 100)}%` : "—";
    const { label: msStatus } = deriveMilestoneUi(i, raw, role);
    return {
      ms: m.label || `Milestone ${i + 1}`,
      pct,
      amt: Math.round(receiveKobo / 100),
      when: msStatus === "Paid" || msStatus === "Released"
        ? msStatus
        : i === 0
          ? "Released when sender confirms this milestone"
          : "Released when sender approves this milestone",
    };
  });
}
